import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { Router, UrlSegment } from '@angular/router';
import { Logger } from '@app/core/logger.service';
import { MediaSession } from '@jofr/capacitor-media-session';
const log = new Logger('AudioMovilService');
export interface Track {
  id: string;
  path: string;
  thumbnail?: string;
  title?: string;
  dateStr?: string;
  description?: string;
}
@Injectable({
  providedIn: 'root'
})
export class AudioMovilService {
  private sound: HTMLAudioElement | null = null;
  private progressInterval?: Subscription;
  private _currentTime = new BehaviorSubject<number>(0);
  private _duration = new BehaviorSubject<number>(0);
  private _onEnded = new BehaviorSubject<any>(undefined);
  private track!: Track;
  public currentTime$ = this._currentTime.asObservable();
  public duration$ = this._duration.asObservable();
  public onEnded$ = this._onEnded.asObservable();
  public isLoading: boolean = false;
  private items: Track[] = [];
  // Movil
  public manually: boolean = false;
  private isPlayingFlag: boolean = false;
  private _soundDuration: number = 0;


  constructor(
    private ngZone: NgZone,
    private router: Router
  ) { }

  async setTrack(track: Track, play: boolean = false): Promise<void> {
    this.isLoading = true;
    if (this.sound) {
      this.stop();
    }
    this.Track = track;
    if (play === true) {
      try {
        this.sound = new Audio(track.path);
        this.sound.preload = 'metadata';

        this.sound.onloadedmetadata = () => {
          this._soundDuration = this.sound!.duration;
          this._duration.next(this._soundDuration);
          const savedProgress = this.getSavedProgress(track.id);
          if (savedProgress > 0) {
            this.sound!.currentTime = savedProgress;
            this._currentTime.next(savedProgress);
            this.initMediaSession(track, this._soundDuration, savedProgress);
          } else {
            this.initMediaSession(track, this._soundDuration, 0);
          }
        };

        this.sound.oncanplay = () => {
          this.isLoading = false;
          this.isPlayingFlag = true;
          this.trackProgress();
          MediaSession.setPlaybackState({ playbackState: 'playing' });
        };

        this.sound.onended = () => {
          if (this.manually === false) {
            this.stopTracking();
            this.clearProgress();
            this._onEnded.next(this.track);
          }
          this.isLoading = false;
          this.manually = false;
          this.isPlayingFlag = false;
        };

        this.sound.onerror = (error) => {
          log.error('Audio error:', error);
          this.isLoading = false;
        };

        await this.sound.play();
      } catch (error) {
        log.error(error);
        this.isLoading = false;
      }
    }
  }

  async togglePlayPause() {
    if (!this.sound) {
      log.warn('No sound object to toggle');
      return;
    }
    try {
      if (this.isPlaying) {
        this.sound.pause();
        this.isPlayingFlag = false;
        await this.saveProgress();
        this.stopTracking();
        MediaSession.setPlaybackState({ playbackState: 'paused' });
      } else {
        await this.sound.play();
        this.isPlayingFlag = true;
        this.trackProgress();
        MediaSession.setPlaybackState({ playbackState: 'playing' });
      }
    } catch (error) {
      log.error('togglePlayPause error:', error);
    }
  }

  stop(): void {
    if (this.sound) {
      this.sound.pause();
      this.sound.currentTime = 0;
      this.saveProgress();
      this.stopTracking();
      this.isPlayingFlag = false;
    }
  }

  async seek(seek: any): Promise<void> {
    if (this.sound) {
      const value: number = Number(seek.detail.value);
      this.sound.currentTime = value;
      this._currentTime.next(value);
      this.saveProgress();
    }
  }

  setRate(rate: number) {
    if (this.sound) {
      this.sound.playbackRate = rate;
    }
  }

  private trackProgress(): void {
    this.stopTracking();
    this.progressInterval = interval(1000).subscribe(() => {
      this.ngZone.run(() => {
        if (this.isPlaying && this.sound) {
          const current = this.sound.currentTime;
          this._currentTime.next(current);
          this.saveProgress();
        }
      });
    });
  }

  private stopTracking() {
    this.progressInterval?.unsubscribe();
  }

  private saveProgress() {
    if (!this.sound || !this.track?.id) return;
    try {
      const position = this.sound.currentTime;
      localStorage.setItem(
        `progress_${this.track.id}`,
        JSON.stringify({ position, duration: this._soundDuration, updated: Date.now() })
      );
    } catch (error) {
      log.warn('No se pudo guardar el progreso:', error);
    }
  }

  private getSavedProgress(trackId: string): number {
    const data = localStorage.getItem(`progress_${trackId}`);
    if (data) {
      try {
        const parsed = JSON.parse(data);
        return parsed.position ?? 0;
      } catch {
        return 0;
      }
    }
    return 0;
  }

  private clearProgress() {
    if (this.track?.id) {
      localStorage.removeItem(`progress_${this.track.id}`);
    }
  }

  private async initMediaSession(track: Track, duration: number, position: number) {
    try {
      await MediaSession.setMetadata({
        title: String(track?.title) || '',
        artist: 'Conectimed ',
        album: 'Podcast',
        artwork: [
          { src: track?.thumbnail || './assets/imgs/logo.png', type: 'image/png', sizes: '512x512' }
        ]
      });
      MediaSession.setPositionState({
        duration: duration,
        position: position
      })
      MediaSession.setPlaybackState({ playbackState: 'playing' });
    } catch (e) {
      log.error("****** enacment:error ******", e);
    }

    MediaSession.setActionHandler({ action: 'play' }, async () => {
      try {
        this.togglePlayPause();
      } catch (error) {
        log.error(error);
      }
    });

    MediaSession.setActionHandler({ action: 'pause' }, async () => {
      try {
        this.togglePlayPause();
      } catch (error) {
        log.error(error);
      }
    });

    MediaSession.setActionHandler({ action: 'nexttrack' }, () => {
      this.nextTrack(true);
    });

    MediaSession.setActionHandler({ action: 'previoustrack' }, () => {
      this.prevTrack();
    });
  }

  get isPlaying(): boolean {
    return this.sound && !this.sound.paused ? true : false;
  }

  get getCurrentSound(): HTMLAudioElement | null {
    return this.sound;
  }

  get Track(): Track {
    return this.track;
  }

  set Track(track: Track) {
    this.track = track;
  }

  set playList(playlist: Track[]) {
    this.items = playlist;
  }

  async prevTrack() {
    if (this.Track && this.Track.id) {
      try {
        const index: number = this.items.map(e => e.id).indexOf(this.Track.id);
        const currentTime: number = this.sound!.currentTime;
        if (currentTime > 3 || index === 0) {
          this.manually = true;
          this.setTrack(this.Track, true);
        } else {
          if (index > -1) {
            const item = this.items[index - 1];
            if (item) {
              this.manually = true;
              this.setTrack(item, true);
              this.navigate(item.id);
            }
          }
        }
      } catch (error) {
        log.error(error);
      }
    }
  }

  async nextTrack(manually: boolean = true) {
    if (this.Track && this.Track.id) {
      const index: number = this.items.map(e => e.id).indexOf(this.Track.id);
      if (index > -1) {
        const item = this.items[index + 1];
        if (item) {
          this.manually = manually;
          this.setTrack(item, true);
          this.navigate(item.id);
        }
      }
    }
  }

  private navigate(nuevoId: string) {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const segments = urlTree.root.children['primary'].segments;
    if (
      segments.length === 3 &&
      segments[0].path === 'podcast' &&
      segments[1].path === 'detail'
    ) {
      segments[2] = new UrlSegment(nuevoId, {});
      const newRoute = '/' + segments.map(s => s.path).join('/');
      const query = urlTree.queryParams;
      const queryString = new URLSearchParams(query).toString();
      const finalUrl = queryString ? `${newRoute}?${queryString}` : newRoute;
      window.history.replaceState({}, '', finalUrl);
    } else {
      log.warn('Routing Error');
    }
  }
}